import axios from 'axios'
import {
  ConversationState,
  PostAgentPromptRequest,
  PostAgentPromptResponse,
  PromptTemplateId,
  StructuredJsonType,
  TileType,
} from 'src/queries/agent/network/agent.models'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'
import { ExtendThreadIDHeader } from 'src/queries/requestConfig.models'

// Backend response structure from /insights endpoint
interface InsightsBackendResponse {
  success: boolean
  canFulfill: boolean // camelCase from backend
  message: string
  tileSpecification?: {
    // camelCase from backend
    id: string
    type: string
    title: string
    endpoint: string
    method: string
    params: Record<string, unknown>
    config: {
      valueKey?: string
      labelKey?: string
      dataArrayKey?: string
      xAxisKey?: string
      yAxisKey?: string
      chartType?: string
      timeGranularity?: string
      customMappings?: Record<string, string>
    }
    created_at: string
    prompt_context: string
  }
  alternativeSuggestion?: string | null // camelCase from backend
  suggestedPrompts?: Array<{
    promptId: string
    displayPrompt: string
  }>
}

export const postInsightsPrompt = async (
  req: PostAgentPromptRequest,
): Promise<PostAgentPromptResponse> => {
  const requestConfig = getRequestConfig(RequestConfig.Agent)

  const { threadId, ...reqRest } = req
  requestConfig.headers = {
    ...requestConfig.headers,
    [ExtendThreadIDHeader]: threadId ?? '',
  }

  const response = await axios.post<InsightsBackendResponse>(
    '/insights',
    reqRest,
    requestConfig,
  )

  // Backend should return thread_id in response header for conversation state
  // If not present, backend needs to be updated to support stateful conversations
  const responseThreadId = response?.headers?.[
    ExtendThreadIDHeader.toLowerCase()
  ] as string | undefined

  const backendData = response.data
  const finalThreadId = responseThreadId || threadId || ''

  return {
    currentState: ConversationState.MESSAGE,
    threadId: finalThreadId,
    response: backendData.message,
    suggestedPrompts: backendData.suggestedPrompts?.map((prompt) => ({
      promptId: PromptTemplateId.None,
      params: {},
      displayPrompt: prompt.displayPrompt,
    })),
    structuredJson: backendData.tileSpecification
      ? {
          type: StructuredJsonType.TILE_DATA,
          data: {
            type: backendData.tileSpecification.type as TileType,
            title: backendData.tileSpecification.title,
            endpoint: backendData.tileSpecification.endpoint,
            method: backendData.tileSpecification.method,
            params: backendData.tileSpecification.params,
            config: backendData.tileSpecification.config,
            promptContext: backendData.tileSpecification.prompt_context,
          },
        }
      : undefined,
  }
}
